/**
 * "Manage all private chats" (01-10-2026, Jordy: "Manage all doet niets").
 *
 * De knop in het paneel "Private chats — all combined" was in het nieuwe
 * dashboard een plaatshouder: hij zette alleen de tabel onderaan op "All".
 * Het oude dashboard had dit venster; de server (`POST
 * /panels/private/bulk`) bestaat nog. Hiermee wijs je in één keer ALLE privé-
 * chats die je mag beheren toe aan een of meer chatters, of geef je ze allemaal
 * vrij naar de gedeelde inbox.
 *
 * Alleen voor wie privé-chats mag beheren (owner/manager, of een chatter met
 * `private_manage_all`); de server controleert dat ook.
 */
import { useState } from 'react'
import { Modal } from './Modal'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'

interface AssignableChatter {
  admin_user_id: number
  name: string
  username: string
  role: string
  is_me: boolean
}

interface BulkAntwoord {
  action: 'assign' | 'release'
  matched_count: number
  changed_count: number
  names?: string[]
}

export function PriveBeheerDialog({ totaal, onClose, onKlaar }: {
  /** Het aantal privé-chats nu (uit het paneel). */
  totaal: number
  onClose: () => void
  onKlaar: () => void
}) {
  const { notify } = useActions()
  const chatters = useApi<{ chatters: AssignableChatter[] }>(
    '/api/admin/conversations/panels/assignable-chatters')
  const [gekozen, setGekozen] = useState<number[]>([])
  const [bezig, setBezig] = useState(false)
  const lijst = chatters.data?.chatters ?? []

  async function doe(actie: 'assign' | 'release') {
    const vraag = actie === 'release'
      ? `Release all ${totaal} private chats back to the shared inbox?`
      : `Assign all ${totaal} private chats to ${gekozen.length} chatter(s)? This replaces the current assignments.`
    if (!window.confirm(vraag)) return
    setBezig(true)
    try {
      const r = await api.post<BulkAntwoord>('/api/admin/conversations/panels/private/bulk', {
        action: actie,
        admin_user_ids: actie === 'assign' ? gekozen : [],
      })
      notify(actie === 'release'
        ? `Released ${r.changed_count} private chats.`
        : `Assigned ${r.changed_count} of ${r.matched_count} private chats.`)
      onKlaar()
      onClose()
    } catch (e) {
      notify(`Manage all: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setBezig(false)
    }
  }

  return (
    <Modal
      title="Manage all private chats"
      desc={`This affects all ${totaal} currently private chats across all platforms and models you can manage.`}
      width={520}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Close</button>
          <button className="btn sm" type="button" style={{ color: '#fbbf24' }}
                  disabled={bezig || totaal === 0}
                  onClick={() => doe('release')}>
            Release all
          </button>
          <button className="btn primary sm" type="button"
                  disabled={bezig || !gekozen.length || totaal === 0}
                  onClick={() => doe('assign')}>
            {gekozen.length ? `Assign all to ${gekozen.length}` : 'Assign all'}
          </button>
        </>
      }
    >
      <div className="mut2" style={{ fontSize: 12, marginBottom: 10 }}>
        Assigning replaces the current private-chat assignments with the selected chatters.
        Releasing returns every private chat to the shared inbox.
      </div>
      {chatters.loading && !lijst.length ? (
        <div className="mut2" style={{ fontSize: 12 }}>Loading chatters…</div>
      ) : null}
      {lijst.map((ch) => {
        const aan = gekozen.includes(ch.admin_user_id)
        return (
          <label key={ch.admin_user_id} className="chkrow" style={{ cursor: 'pointer', display: 'flex' }}
                 onClick={() => setGekozen((s) => (aan ? s.filter((x) => x !== ch.admin_user_id)
                                                     : [...s, ch.admin_user_id]))}>
            <span className={aan ? 'chk on' : 'chk'}>{aan ? '✓' : ''}</span>
            {ch.name} <span className="mut2">({ch.role}){ch.is_me ? ' — you' : ''}</span>
          </label>
        )
      })}
    </Modal>
  )
}
